import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../SafeIcon';
import supabase from '../../../supabase/supabase';

const {
  FiPlus, FiX, FiCheck, FiSearch, FiShoppingBag,
  FiAlertCircle, FiTrash2, FiEdit2, FiHash, FiCreditCard, FiUser, FiMapPin, FiPhone, FiMail
} = FiIcons;

const STATUSES = ['pending', 'completed', 'cancelled', 'refunded'];

const getStatusColors = (status, isDark) => {
  const colors = {
    completed: isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700',
    pending: isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
    cancelled: isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600',
    refunded: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500',
  };
  return colors[status] || (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500');
};

const ContextMenu = ({ x, y, onClose, onDelete, onClientDetails, isDark }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <motion.div
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`fixed z-[150] rounded-xl shadow-2xl border overflow-hidden ${
        isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-200'
      }`}
      style={{ top: y, left: x }}
    >
      <button
        onClick={onClientDetails}
        className={`w-full px-4 py-3 text-left text-sm font-bold flex items-center space-x-3 transition-colors ${
          isDark ? 'text-gray-200 hover:bg-[#334155]' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <SafeIcon icon={FiUser} className="w-4 h-4" />
        <span>Client Details</span>
      </button>
      <button
        onClick={onDelete}
        className={`w-full px-4 py-3 text-left text-sm font-bold flex items-center space-x-3 transition-colors ${
          isDark ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
        }`}
      >
        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
        <span>Delete</span>
      </button>
    </motion.div>
  );
};

const ClientDetailsModal = ({ order, onClose, isDark }) => {
  if (!order) return null;

  const bg = isDark ? 'bg-[#1e293b]' : 'bg-white';
  const border = isDark ? 'border-[#334155]' : 'border-gray-100';
  const headerBg = isDark ? 'bg-[#0f172a]' : 'bg-gray-50';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-500' : 'text-gray-400';
  const valueColor = isDark ? 'text-gray-200' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#0f172a]' : 'bg-gray-50';

  const InfoRow = ({ icon, label, value }) => (
    <div className={`p-4 rounded-xl ${sectionBg} flex items-start space-x-3`}>
      <SafeIcon icon={icon} className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${labelColor}`}>{label}</p>
        <p className={`text-sm font-medium break-words ${valueColor}`}>{value || '—'}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border ${bg} ${border}`}
      >
        {/* Header */}
        <div className={`p-6 border-b flex justify-between items-center ${headerBg} ${border}`}>
          <div>
            <h3 className={`font-black text-xl ${textColor}`}>Client Details</h3>
            <p className={`text-sm mt-1 ${labelColor}`}>
              Order #{order.order_number || 'N/A'}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-xl transition-colors ${
              isDark ? 'hover:bg-[#334155]' : 'hover:bg-gray-200'
            }`}
          >
            <SafeIcon icon={FiX} className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-6">
          
          {/* Customer Information */}
          <div>
            <h4 className={`text-sm font-black uppercase tracking-wider mb-3 flex items-center space-x-2 ${labelColor}`}>
              <SafeIcon icon={FiUser} className="w-4 h-4" />
              <span>Customer Information</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoRow icon={FiUser} label="Full Name" value={order.customer_name} />
              <InfoRow icon={FiMail} label="Email Address" value={order.customer_email} />
              <InfoRow icon={FiPhone} label="Phone Number" value={order.customer_phone} />
              {order.billing_company && (
                <InfoRow icon={FiUser} label="Company Name" value={order.billing_company} />
              )}
            </div>
          </div>

          {/* Billing Address */}
          <div>
            <h4 className={`text-sm font-black uppercase tracking-wider mb-3 flex items-center space-x-2 ${labelColor}`}>
              <SafeIcon icon={FiMapPin} className="w-4 h-4" />
              <span>Billing Address</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <InfoRow icon={FiMapPin} label="Street Address" value={order.billing_address} />
              </div>
              <InfoRow icon={FiMapPin} label="City" value={order.billing_city} />
              <InfoRow icon={FiMapPin} label="Zip / Postal Code" value={order.billing_zip} />
              <InfoRow icon={FiMapPin} label="Country" value={order.billing_country} />
              {order.billing_state && (
                <InfoRow icon={FiMapPin} label="State / Province" value={order.billing_state} />
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <h4 className={`text-sm font-black uppercase tracking-wider mb-3 flex items-center space-x-2 ${labelColor}`}>
              <SafeIcon icon={FiCreditCard} className="w-4 h-4" />
              <span>Order Summary</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoRow icon={FiHash} label="Order Number" value={order.order_number} />
              <InfoRow icon={FiCreditCard} label="Payment Method" value={order.payment_method || 'None'} />
              <div className={`p-4 rounded-xl ${sectionBg} flex items-start space-x-3`}>
                <SafeIcon icon={FiCreditCard} className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <div className="flex-1">
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${labelColor}`}>Order Total</p>
                  <p className={`text-2xl font-black ${textColor}`}>R{Number(order.total).toFixed(2)}</p>
                </div>
              </div>
              <div className={`p-4 rounded-xl ${sectionBg} flex items-start space-x-3`}>
                <SafeIcon icon={FiCheck} className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <div className="flex-1">
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${labelColor}`}>Order Status</p>
                  <span className={`inline-block px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider ${getStatusColors(order.status, isDark)}`}>
                    {order.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className={`p-6 border-t ${headerBg} ${border}`}>
          <button
            onClick={onClose}
            className="w-full py-3 bg-[#22c55e] text-white font-bold rounded-xl hover:bg-green-600 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const OrderModal = ({ order, onClose, onSave, projectId, isDark }) => {
  const [form, setForm] = useState(
    order || { customer_name: '', customer_email: '', total: '', status: 'pending', payment_method: '', payment_status: 'pending' }
  );
  const [saving, setSaving] = useState(false);
  const isEdit = !!order?.id;

  const handleSave = async () => {
    if (!form.customer_name.trim() || !form.total) return;
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        total: parseFloat(form.total),
        status: form.status,
        payment_method: form.payment_method || null,
        payment_status: form.payment_status || 'pending',
        project_id: projectId,
      };
      if (isEdit) {
        await supabase
          .from('store_orders_1772955957776')
          .update(payload)
          .eq('id', order.id)
          .eq('project_id', projectId);
      } else {
        await supabase.from('store_orders_1772955957776').insert(payload);
      }
      onSave();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = isDark
    ? "w-full p-3 bg-[#0f172a] border border-[#334155] rounded-xl outline-none focus:ring-2 focus:ring-green-500/20 text-sm text-gray-200"
    : "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-green-500/20 text-sm";
  const labelClass = `text-[10px] font-black uppercase tracking-widest block mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border ${isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-transparent'}`}
      >
        <div className={`p-5 border-b flex justify-between items-center ${isDark ? 'border-[#334155]' : 'border-gray-100'}`}>
          <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{isEdit ? 'Edit Order' : 'Add Order'}</h3>
          <button onClick={onClose} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#334155]' : 'hover:bg-gray-100'}`}>
            <SafeIcon icon={FiX} className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
          </button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
          {[
            { label: 'Customer Name *', key: 'customer_name', placeholder: 'John Doe' },
            { label: 'Customer Email', key: 'customer_email', placeholder: 'john@example.com', type: 'email' },
            { label: 'Order Total (R) *', key: 'total', placeholder: '0.00', type: 'number' },
          ].map(f => (
            <div key={f.key}>
              <label className={labelClass}>{f.label}</label>
              <input
                type={f.type || 'text'}
                value={form[f.key]}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className={inputClass}
              />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Order Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className={`${inputClass} capitalize cursor-pointer`}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Paid Status</label>
              <select value={form.payment_status || 'pending'} onChange={e => setForm({ ...form, payment_status: e.target.value })} className={`${inputClass} capitalize cursor-pointer`}>
                <option value="pending">Pending</option>
                <option value="successful">Successful</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Payment Method</label>
              <select value={form.payment_method || ''} onChange={e => setForm({ ...form, payment_method: e.target.value })} className={`${inputClass} capitalize cursor-pointer`}>
                <option value="">None</option>
                <option value="payfast">PayFast</option>
                <option value="payjustnow">PayJustNow</option>
                <option value="payflex">Payflex</option>
                <option value="float">Float</option>
                <option value="happypay">Happy Pay</option>
              </select>
            </div>
          </div>
        </div>
        <div className={`p-5 flex space-x-3 border-t ${isDark ? 'bg-[#0f172a] border-[#334155]' : 'bg-gray-50 border-gray-100'}`}>
          <button onClick={onClose} className={`flex-1 py-3 font-bold rounded-xl transition-colors text-sm ${isDark ? 'text-gray-400 hover:bg-[#1e293b]' : 'text-gray-500 hover:bg-gray-200'}`}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.customer_name.trim() || !form.total}
            className="flex-[2] py-3 bg-[#22c55e] text-white font-bold rounded-xl hover:bg-green-600 transition-colors text-sm disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {saving
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <><SafeIcon icon={FiCheck} className="w-4 h-4" /><span>{isEdit ? 'Update' : 'Add Order'}</span></>
            }
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const OrderDetailPanel = ({ order, onClose, onStatusChange, onPaymentStatusChange, isDark }) => {
  if (!order) return null;
  const statusColor = getStatusColors(order.status, isDark);
  const paymentStatusColor = getStatusColors(order.payment_status === 'successful' ? 'completed' : order.payment_status === 'failed' ? 'cancelled' : 'pending', isDark);
  
  const bg = isDark ? 'bg-[#1e293b]' : 'bg-white';
  const border = isDark ? 'border-[#334155]' : 'border-gray-100';
  const headerBg = isDark ? 'bg-[#0f172a]' : 'bg-gray-50';
  const panelBg = isDark ? 'bg-[#0f172a]' : 'bg-gray-50';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-500' : 'text-gray-400';

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      className={`w-80 shrink-0 border-l flex flex-col h-full shadow-2xl z-10 ${bg} ${border}`}
    >
      <div className={`p-5 border-b flex items-center justify-between ${headerBg} ${border}`}>
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest ${labelColor}`}>Order Details</p>
          <p className={`font-black text-lg mt-0.5 ${textColor}`}>
            {order.order_number || <span className={`font-normal text-sm ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>No order #</span>}
          </p>
        </div>
        <button onClick={onClose} className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-[#334155]' : 'hover:bg-gray-200'}`}>
          <SafeIcon icon={FiX} className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-6 custom-scrollbar">
        <div className={`border rounded-xl p-4 flex items-center space-x-3 ${isDark ? 'bg-green-900/10 border-green-900/30' : 'bg-green-50 border-green-100'}`}>
          <div className="w-9 h-9 bg-[#22c55e] rounded-lg flex items-center justify-center shrink-0">
            <SafeIcon icon={FiHash} className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className={`text-[10px] font-black uppercase tracking-widest ${labelColor}`}>Order Number</p>
            <p className="font-black text-[#22c55e] text-base">{order.order_number || '—'}</p>
          </div>
        </div>
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${labelColor}`}>Customer</p>
          <div className={`rounded-xl p-4 space-y-2 ${panelBg}`}>
            <p className={`font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.customer_name}</p>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{order.customer_email || '—'}</p>
          </div>
        </div>
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${labelColor}`}>Order Total</p>
          <div className={`rounded-xl p-4 ${panelBg}`}>
            <p className={`text-2xl font-black ${textColor}`}>R{Number(order.total).toFixed(2)}</p>
          </div>
        </div>
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${labelColor}`}>Payment Method</p>
          <div className={`rounded-xl p-4 flex items-center space-x-3 ${panelBg}`}>
            <SafeIcon icon={FiCreditCard} className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className={`text-sm font-bold capitalize ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {order.payment_method || 'None / Pending'}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${labelColor}`}>Order Status</p>
            <select
              value={order.status}
              onChange={e => onStatusChange(order.id, e.target.value)}
              className={`w-full px-3 py-3 rounded-xl font-black text-sm border-0 outline-none cursor-pointer capitalize ${statusColor}`}
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${labelColor}`}>Paid Status</p>
            <select
              value={order.payment_status || 'pending'}
              onChange={e => onPaymentStatusChange(order.id, e.target.value)}
              className={`w-full px-3 py-3 rounded-xl font-black text-sm border-0 outline-none cursor-pointer capitalize ${paymentStatusColor}`}
            >
              <option value="pending">Pending</option>
              <option value="successful">Successful</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${labelColor}`}>Date Placed</p>
          <div className={`rounded-xl p-4 ${panelBg}`}>
            <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {new Date(order.created_at).toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              })}
            </p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {new Date(order.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function OrdersPanel({ projectId, isDark = false }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('orders'); // 'orders' | 'abandoned'
  const [modal, setModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [clientDetailsOrder, setClientDetailsOrder] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);

  useEffect(() => {
    if (projectId) fetchOrders();
  }, [projectId]);

  const fetchOrders = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('store_orders_1772955957776')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    await supabase
      .from('store_orders_1772955957776')
      .delete()
      .eq('id', id)
      .eq('project_id', projectId);
    setOrders(o => o.filter(x => x.id !== id));
    setDeleteConfirm(null);
    if (selectedOrder?.id === id) setSelectedOrder(null);
  };

  const handleStatusChange = async (id, status) => {
    await supabase
      .from('store_orders_1772955957776')
      .update({ status })
      .eq('id', id)
      .eq('project_id', projectId);
    setOrders(o => o.map(x => x.id === id ? { ...x, status } : x));
    setSelectedOrder(prev => prev?.id === id ? { ...prev, status } : prev);
  };

  const handlePaymentStatusChange = async (id, payment_status) => {
    await supabase
      .from('store_orders_1772955957776')
      .update({ payment_status })
      .eq('id', id)
      .eq('project_id', projectId);
    setOrders(o => o.map(x => x.id === id ? { ...x, payment_status } : x));
    setSelectedOrder(prev => prev?.id === id ? { ...prev, payment_status } : prev);
  };

  const handleContextMenu = (e, order) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, order });
  };

  const sendConfirmationEmail = async (order) => {
    if (!order.customer_email || order.customer_email.trim() === '') {
      alert('Cannot send email: This order does not have a valid customer email address.');
      return;
    }

    if (!window.confirm(`Send confirmation email to ${order.customer_email}?`)) return;
    
    setSendingEmailId(order.id);
    try {
      // Fetch credentials from the NEW store_email_configs table
      const { data: credData } = await supabase.from('store_email_configs')
        .select('service_id, template_id, public_key').eq('project_id', projectId).maybeSingle();
        
      if (!credData?.service_id || !credData?.template_id || !credData?.public_key) {
        alert('EmailJS credentials are not configured. Please set them up in Store Settings > EmailJS Integration.');
        setSendingEmailId(null);
        return;
      }

      // Fetch store details to get the orderEmail for reply_to
      const { data: detailsData } = await supabase.from('store_settings_1773100000000')
        .select('value').eq('project_id', projectId).eq('key', 'store_details').maybeSingle();
      
      const storeEmail = detailsData?.value?.orderEmail || '';
      const storeName = detailsData?.value?.companyName || 'Store';

      const { data: items } = await supabase.from('store_order_items_1772955957776')
        .select('*, product:product_id(name)')
        .eq('order_id', order.id);

      const itemsList = (items || []).map(i => `${i.quantity}x ${i.product?.name || 'Product'} - R${Number(i.price).toFixed(2)}`).join('\n');

      const payload = {
        service_id: credData.service_id,
        template_id: credData.template_id,
        user_id: credData.public_key,
        template_params: {
          to_name: order.customer_name,
          to_email: order.customer_email,
          // We include multiple common variables because we don't know exactly what 
          // variable name the user put inside their EmailJS template (e.g. {{email}} or {{user_email}})
          email: order.customer_email,
          customer_email: order.customer_email,
          user_email: order.customer_email,
          reply_to: storeEmail,
          store_email: storeEmail,
          store_name: storeName,
          order_number: order.order_number,
          order_total: `R${Number(order.total).toFixed(2)}`,
          order_items: itemsList,
          payment_method: order.payment_method || 'None',
          payment_status: order.payment_status || 'pending',
          message: itemsList // Catch-all in case they mapped {{message}} to the order contents
        }
      };

      const res = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`EmailJS Error: ${errorText}`);
      }

      await supabase.from('store_orders_1772955957776')
        .update({ confirmation_email_sent: true })
        .eq('id', order.id);

      setOrders(o => o.map(x => x.id === order.id ? { ...x, confirmation_email_sent: true } : x));
      alert(`Confirmation email sent to ${order.customer_email}`);
    } catch (err) {
      console.error('Failed to send email:', err);
      alert('Failed to send email: ' + err.message);
    } finally {
      setSendingEmailId(null);
    }
  };

  const filtered = orders.filter(o => {
    const matchSearch =
      o.customer_name.toLowerCase().includes(search.toLowerCase()) ||
      o.customer_email?.toLowerCase().includes(search.toLowerCase()) ||
      (o.order_number || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    
    // Normal orders = successful only, Abandoned = failed OR pending
    const matchView = viewMode === 'abandoned' 
      ? (o.payment_status === 'failed' || o.payment_status === 'pending') 
      : (o.payment_status === 'successful');
      
    return matchSearch && matchStatus && matchView;
  });

  const totalRevenue = filtered
    .filter(o => o.status === 'completed' || o.payment_status === 'successful')
    .reduce((s, o) => s + Number(o.total), 0);

  const bg = isDark ? 'bg-[#0f172a]' : 'bg-[#fafbfc]';
  const cardBg = isDark ? 'bg-[#1e293b]' : 'bg-white';
  const border = isDark ? 'border-[#334155]' : 'border-gray-100';
  const titleColor = isDark ? 'text-white' : 'text-gray-800';
  const inputBg = isDark ? 'bg-[#1e293b]' : 'bg-white';
  const inputBorder = isDark ? 'border-[#334155]' : 'border-gray-200';
  const inputText = isDark ? 'text-gray-200' : 'text-gray-800';

  return (
    <div className={`flex-1 flex overflow-hidden ${bg}`}>
      {/* Main Panel */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-2xl font-bold ${titleColor}`}>{viewMode === 'abandoned' ? 'Abandoned Carts' : 'Orders'}</h1>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>
                {filtered.length} {viewMode === 'abandoned' ? 'carts' : 'orders'} {viewMode === 'orders' ? `· R${totalRevenue.toFixed(2)} revenue` : ''}
              </p>
            </div>
            <div className="flex space-x-3">
              <div className={`p-1 flex rounded-xl border ${isDark ? 'bg-[#0f172a] border-[#334155]' : 'bg-gray-50 border-gray-200'}`}>
                <button
                  onClick={() => setViewMode('orders')}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                    viewMode === 'orders'
                      ? (isDark ? 'bg-[#334155] text-white' : 'bg-white text-gray-800 shadow-sm')
                      : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                  }`}
                >
                  Normal Orders
                </button>
                <button
                  onClick={() => setViewMode('abandoned')}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                    viewMode === 'abandoned'
                      ? (isDark ? 'bg-[#334155] text-white' : 'bg-white text-gray-800 shadow-sm')
                      : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                  }`}
                >
                  Abandoned Carts
                </button>
              </div>
              <button
                onClick={() => setModal({})}
                className="bg-[#22c55e] hover:bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm flex items-center space-x-2 transition-colors shadow-lg shadow-green-500/20"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" /><span>Add Order</span>
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email or order number..."
                className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-500/20 transition-colors shadow-sm ${inputBg} ${inputBorder} border ${inputText}`}
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className={`py-2.5 px-4 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-500/20 transition-colors capitalize cursor-pointer border ${inputBg} ${inputBorder} ${inputText}`}
            >
              <option value="all">All Status</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className={`text-center py-16 ${isDark ? 'text-gray-500' : 'text-gray-300'}`}>
              <SafeIcon icon={FiShoppingBag} className="w-12 h-12 mx-auto mb-3" />
              <p className="font-medium">No {viewMode === 'abandoned' ? 'abandoned carts' : 'orders'} found</p>
            </div>
          ) : (
            <div className={`rounded-2xl border shadow-sm overflow-hidden ${cardBg} ${border}`}>
              <table className="w-full">
                <thead className={isDark ? 'bg-[#0f172a]' : 'bg-gray-50/80'}>
                  <tr>
                    {['Order #', 'Customer', 'Total', 'Payment', 'Paid', 'Status', 'Date', 'Email', 'Actions'].map(h => (
                      <th key={h} className={`text-left text-[10px] font-black uppercase tracking-wider px-5 py-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-[#334155]' : 'divide-gray-50'}`}>
                  {filtered.map(o => (
                    <tr
                      key={o.id}
                      onClick={() => setSelectedOrder(o)}
                      onContextMenu={(e) => handleContextMenu(e, o)}
                      className={`transition-colors cursor-pointer ${
                        selectedOrder?.id === o.id 
                          ? (isDark ? 'bg-[#334155]/50' : 'bg-green-50/40') 
                          : (isDark ? 'hover:bg-[#334155]/30' : 'hover:bg-gray-50/50')
                      }`}
                    >
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg font-black text-xs ${isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-600'}`}>
                          <SafeIcon icon={FiHash} className="w-3 h-3" />
                          <span>{o.order_number || '—'}</span>
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{o.customer_name}</div>
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{o.customer_email}</div>
                      </td>
                      <td className={`px-5 py-4 font-bold text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        R{Number(o.total).toFixed(2)}
                      </td>
                      <td className={`px-5 py-4 text-sm capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {o.payment_method || '—'}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          o.payment_status === 'successful' ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700') :
                          o.payment_status === 'failed' ? (isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600') :
                          (isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                        }`}>
                          {o.payment_status || 'pending'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getStatusColors(o.status, isDark)}`}>
                          {o.status}
                        </span>
                      </td>
                      <td className={`px-5 py-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-5 py-4">
                        <div onClick={e => e.stopPropagation()}>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              await sendConfirmationEmail(o);
                            }}
                            disabled={sendingEmailId === o.id || !o.customer_email}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1.5 border ${
                              !o.customer_email ? 'opacity-50 cursor-not-allowed border-gray-200 text-gray-400' :
                              o.confirmation_email_sent 
                                ? (isDark ? 'bg-blue-900/20 text-blue-400 border-blue-900/30' : 'bg-blue-50 text-blue-600 border-blue-200') 
                                : (isDark ? 'border-[#334155] text-gray-300 hover:bg-[#334155]' : 'border-gray-200 text-gray-700 hover:bg-gray-100')
                            }`}
                          >
                            {sendingEmailId === o.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <SafeIcon icon={o.confirmation_email_sent ? FiCheck : FiMail} className="w-3.5 h-3.5" />
                                <span>{o.confirmation_email_sent ? 'Sent' : 'Send'}</span>
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center space-x-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setModal(o)}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-500 hover:text-green-400 hover:bg-green-400/10' : 'text-gray-400 hover:text-green-500 hover:bg-green-50'}`}
                          >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(o)}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-500 hover:text-red-400 hover:bg-red-400/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Side Panel */}
      <AnimatePresence>
        {selectedOrder && (
          <OrderDetailPanel
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onStatusChange={handleStatusChange}
            onPaymentStatusChange={handlePaymentStatusChange}
            isDark={isDark}
          />
        )}
      </AnimatePresence>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onDelete={() => {
              setDeleteConfirm(contextMenu.order);
              setContextMenu(null);
            }}
            onClientDetails={() => {
              setClientDetailsOrder(contextMenu.order);
              setContextMenu(null);
            }}
            isDark={isDark}
          />
        )}
      </AnimatePresence>

      {/* Modals */}
      <AnimatePresence>
        {modal !== null && (
          <OrderModal
            order={modal?.id ? modal : null}
            onClose={() => setModal(null)}
            onSave={fetchOrders}
            projectId={projectId}
            isDark={isDark}
          />
        )}
        {clientDetailsOrder && (
          <ClientDetailsModal
            order={clientDetailsOrder}
            onClose={() => setClientDetailsOrder(null)}
            isDark={isDark}
          />
        )}
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className={`rounded-2xl p-6 max-w-sm w-full shadow-2xl border ${isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-transparent'}`}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-900/30 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Delete Order?</h4>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This cannot be undone</p>
                </div>
              </div>
              <p className={`text-sm mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Delete order <strong className={isDark ? 'text-white' : 'text-gray-900'}>{deleteConfirm.order_number || ''}</strong> from{' '}
                <strong className={isDark ? 'text-white' : 'text-gray-900'}>{deleteConfirm.customer_name}</strong>?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className={`flex-1 py-2.5 font-bold rounded-xl transition-colors text-sm ${isDark ? 'text-gray-400 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 py-2.5 bg-red-50 text-white font-bold rounded-xl hover:bg-red-600 transition-colors text-sm flex items-center justify-center space-x-1"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" /><span>Delete</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}